export const M9_UNIT_16 = {
  id: "m9-u16-adding-information",
  title: "Unidad 16: Adding Information - Non-defining Relative Clauses",
  topic: "Add extra details with style! Learn non-defining relative clauses with commas to give bonus information about people, places, and things. Perfect for rich, descriptive English!",
  iconType: "info-circle",
  grammarImage: "/grammar/grammar-m9-u16.jpg",
  grammarText: "Non-defining Relative Clauses add EXTRA information! Use commas: My boss, who is very supportive, gave me a raise. WHO for people, WHICH for things. The info is BONUS - not essential!",
  
  sets: [
    {
      id: "m9-u16-s1",
      title: "Set 1: Introduction to Non-Defining Clauses 📌",
      context: `**Email to a Friend**

"My colleague Sarah, who is from Canada, speaks three languages. The office, which is near the park, has amazing views."

Notice: The information between commas is EXTRA - nice to know, but not essential!`,
      
      questions: [
        {
          type: "mc",
          q: "What makes a clause 'non-defining'?",
          options: [
            "It gives extra information that's not essential",
            "It gives essential information we need",
            "It never uses WHO or WHICH"
          ],
          a: "It gives extra information that's not essential",
          tip: "Think: bonus information!",
          successtip: "✓ EXTRA INFO!\nNon-defining clauses add bonus details - nice to have, not essential! 📌"
        },
        {
          type: "match",
          q: "Match the examples:",
          pairs: [
            { left: "My sister, who lives in Tokyo,", right: "is visiting next week" },
            { left: "The car, which is red,", right: "belongs to my dad" },
            { left: "John, who is a doctor,", right: "works at the hospital" }
          ],
          a: "completed",
          tip: "Notice the commas!",
          successtip: "✓ COMMA STRUCTURE!\nCommas separate the extra information! Key feature! ✨"
        },
        {
          type: "mc",
          q: "The key punctuation for non-defining clauses is:",
          options: [
            "Commas (,)",
            "Periods (.)",
            "No punctuation needed"
          ],
          a: "Commas (,)",
          successtip: "✓ COMMAS ARE KEY!\nNon-defining clauses ALWAYS use commas! Essential rule! ✏️"
        },
        {
          type: "input",
          q: "Complete: My manager, _____ is very supportive, gave me a raise.",
          a: "who",
          alts: [],
          tip: "Manager = person!",
          successtip: "✓ WHO FOR PEOPLE!\nUse WHO in non-defining clauses for people! 👤"
        },
        {
          type: "order",
          q: "Arrange: London, / I / which / love, / is / visited / last / year",
          words: ["London,", "which", "I", "visited", "last", "year,", "is", "beautiful"],
          a: "London, which I visited last year, is beautiful",
          extra: ["that", "who"],
          tip: "Notice the commas!",
          successtip: "✓ PLACE DESCRIPTION!\nAdding extra detail about London! Commas surround the clause! 🌍"
        },
        {
          type: "mc",
          q: "In 'My car, which is blue, needs repairs', can we remove 'which is blue'?",
          options: [
            "Yes, the sentence still makes sense",
            "No, we need it to identify the car",
            "Only in formal writing"
          ],
          a: "Yes, the sentence still makes sense",
          tip: "Extra information = optional!",
          successtip: "✓ REMOVABLE INFO!\nNon-defining clauses can be removed - the sentence still works! 🚗"
        },
        {
          type: "match",
          q: "Identify what's in the non-defining clause:",
          pairs: [
            { left: "My friend, who is a chef,", right: "WHO IS A CHEF = extra info" },
            { left: "The book, which costs $20,", right: "WHICH COSTS $20 = extra info" },
            { left: "Paris, which I love,", right: "WHICH I LOVE = extra info" }
          ],
          a: "completed",
          successtip: "✓ IDENTIFYING EXTRAS!\nYou can spot the bonus information! Reading skill! 👀"
        },
        {
          type: "input",
          q: "Complete: The office, _____ has a great view, is downtown.",
          a: "which",
          alts: [],
          tip: "Office = thing!",
          successtip: "✓ WHICH FOR THINGS!\nUse WHICH in non-defining clauses for things and places! 🏢"
        },
        {
          type: "mc",
          q: "Which is correctly punctuated?",
          options: [
            "My sister, who lives in Spain, is a teacher",
            "My sister who lives in Spain, is a teacher",
            "My sister, who lives in Spain is a teacher"
          ],
          a: "My sister, who lives in Spain, is a teacher",
          tip: "Commas on BOTH sides!",
          successtip: "✓ BOTH COMMAS!\nNon-defining clauses need commas on BOTH sides! Perfect punctuation! ✅"
        },
        {
          type: "order",
          q: "Translate: Mi hermano, que vive en México, es ingeniero",
          words: ["My", "brother,", "who", "lives", "in", "Mexico,", "is", "an", "engineer"],
          a: "My brother, who lives in Mexico, is an engineer",
          extra: ["that", "which"],
          successtip: "✓ FAMILY DESCRIPTION!\nAdding extra info about family! Natural and descriptive! 👨‍👩‍👦"
        }
      ]
    },

    {
      id: "m9-u16-s2",
      title: "Set 2: Defining vs Non-Defining 🔍",
      context: `**Understanding the Difference**

DEFINING (no commas): "The student who sits next to me is from China." (Which student? The one who sits next to me!)

NON-DEFINING (with commas): "Tom, who sits next to me, is from China." (Extra info about Tom)`,
      
      questions: [
        {
          type: "mc",
          q: "What's the main difference?",
          options: [
            "Defining = essential info (no commas), Non-defining = extra info (commas)",
            "Defining = with commas, Non-defining = no commas",
            "They are exactly the same"
          ],
          a: "Defining = essential info (no commas), Non-defining = extra info (commas)",
          successtip: "✓ KEY DIFFERENCE!\nDefining = must have it. Non-defining = nice bonus! 🎯"
        },
        {
          type: "match",
          q: "Classify these clauses:",
          pairs: [
            { left: "The teacher who helped me", right: "DEFINING - no commas" },
            { left: "Mrs. Smith, who is my teacher,", right: "NON-DEFINING - commas" },
            { left: "A job that pays well", right: "DEFINING - no commas" }
          ],
          a: "completed",
          successtip: "✓ CLASSIFICATION SKILL!\nYou can tell them apart! Essential vs extra! 📊"
        },
        {
          type: "mc",
          q: "'Students who study hard succeed' - Why no commas?",
          options: [
            "We're defining WHICH students - the ones who study hard",
            "It's about all students in general",
            "Commas are optional here"
          ],
          a: "We're defining WHICH students - the ones who study hard",
          tip: "Specifying which ones!",
          successtip: "✓ DEFINING PURPOSE!\nThe clause identifies WHICH students - essential! 🎓"
        },
        {
          type: "mc",
          q: "'My laptop, which is new, works great' - Why commas?",
          options: [
            "Extra info - we already know which laptop (mine)",
            "We need it to identify the laptop",
            "All laptop sentences use commas"
          ],
          a: "Extra info - we already know which laptop (mine)",
          tip: "'My laptop' already identifies it!",
          successtip: "✓ ALREADY IDENTIFIED!\n'My laptop' is specific - WHICH IS NEW is just bonus info! 💻"
        },
        {
          type: "input",
          q: "Does this need commas? 'People _____ work hard succeed'\nAnswer: yes or no",
          a: "no",
          alts: [],
          tip: "Are we defining which people?",
          successtip: "✓ NO COMMAS!\nDefining WHICH people (those who work hard) - essential info! ✅"
        },
        {
          type: "input",
          q: "Does this need commas? 'My father _____ lives in Boston _____ is retired'\nAnswer with commas: , or nothing",
          a: ",",
          alts: [],
          tip: "'My father' already identifies him!",
          successtip: "✓ COMMAS NEEDED!\n'My father' is already specific - extra info needs commas! 👴"
        },
        {
          type: "match",
          q: "Why commas or no commas?",
          pairs: [
            { left: "The book that I'm reading", right: "DEFINING - which book?" },
            { left: "This book, which I bought,", right: "NON-DEFINING - THIS already identifies" },
            { left: "Anyone who wants to come", right: "DEFINING - which people?" }
          ],
          a: "completed",
          successtip: "✓ LOGICAL REASONING!\nYou understand WHY we use or don't use commas! 🧠"
        },
        {
          type: "order",
          q: "Add commas where needed: My car which is ten years old still runs well",
          words: ["My", "car,", "which", "is", "ten", "years", "old,", "still", "runs", "well"],
          a: "My car, which is ten years old, still runs well",
          extra: ["that"],
          tip: "'My car' already identifies it!",
          successtip: "✓ COMMA PLACEMENT!\n'My car' is specific - age is extra detail! Perfect! 🚗"
        },
        {
          type: "mc",
          q: "'The employees who arrived late' vs 'The employees, who arrived late' - difference?",
          options: [
            "First = SOME employees. Second = ALL employees",
            "They mean exactly the same",
            "First = ALL employees. Second = SOME employees"
          ],
          a: "First = SOME employees. Second = ALL employees",
          tip: "Huge meaning difference!",
          successtip: "✓ MEANING MATTERS!\nNo commas = SOME (defining). Commas = ALL (extra info)! Critical! ⚠️"
        },
        {
          type: "match",
          q: "Match the meaning:",
          pairs: [
            { left: "My sister who lives in NYC", right: "I have more than one sister" },
            { left: "My sister, who lives in NYC,", right: "I have only one sister" },
            { left: "Cars that are electric", right: "Some cars (not all)" }
          ],
          a: "completed",
          tip: "Commas change meaning!",
          successtip: "✓ MEANING SHIFT!\nCommas completely change what you're saying! Powerful! 💡"
        }
      ]
    },

    {
      id: "m9-u16-s3",
      title: "Set 3: WHO in Non-Defining Clauses 👥",
      context: `**Describing People with Extra Details**

"My colleague Anna, who joined last month, is very talented. The CEO, who founded the company, is retiring next year."

Remember: Use WHO for people in non-defining clauses!`,
      
      questions: [
        {
          type: "mc",
          q: "For people in non-defining clauses, use:",
          options: [
            "WHO (never THAT)",
            "THAT (never WHO)",
            "Either WHO or THAT"
          ],
          a: "WHO (never THAT)",
          tip: "THAT doesn't work in non-defining!",
          successtip: "✓ WHO ONLY!\nIn non-defining clauses, use WHO for people - NEVER THAT! 👤"
        },
        {
          type: "match",
          q: "Complete with WHO:",
          pairs: [
            { left: "My boss, who is Italian,", right: "speaks four languages" },
            { left: "Sarah, who I met yesterday,", right: "is joining our team" },
            { left: "The professor, who is brilliant,", right: "won an award" }
          ],
          a: "completed",
          successtip: "✓ WHO FOR PEOPLE!\nAdding personality and background info! Descriptive! ✨"
        },
        {
          type: "input",
          q: "Complete: My neighbor, _____ is a doctor, helped me yesterday.",
          a: "who",
          alts: [],
          tip: "Neighbor = person!",
          successtip: "✓ WHO IN ACTION!\nAdding helpful extra information about your neighbor! 🏘️"
        },
        {
          type: "order",
          q: "Translate: Mi amigo David, que es chef, abrió un restaurante",
          words: ["My", "friend", "David,", "who", "is", "a", "chef,", "opened", "a", "restaurant"],
          a: "My friend David, who is a chef, opened a restaurant",
          extra: ["that", "which"],
          successtip: "✓ PROFESSIONAL INFO!\nSharing someone's profession as extra detail! Natural! 👨‍🍳"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "My sister, that lives in Paris, is happy",
            "My sister, who lives in Paris, is happy",
            "Both are equally correct"
          ],
          a: "My sister, that lives in Paris, is happy",
          tip: "Can't use THAT with commas!",
          successtip: "✓ NO THAT!\nNEVER use THAT in non-defining clauses! Only WHO/WHICH! ❌"
        },
        {
          type: "match",
          q: "Add information about people:",
          pairs: [
            { left: "The manager, who started here,", right: "has 20 years experience" },
            { left: "My parents, who are retired,", right: "travel frequently" },
            { left: "Tom, who you met,", right: "got promoted" }
          ],
          a: "completed",
          successtip: "✓ EXTRA DETAILS!\nEnriching descriptions with background information! 🎨"
        },
        {
          type: "input",
          q: "Complete: The author, _____ I admire greatly, is speaking tonight.",
          a: "who",
          alts: ["whom"],
          tip: "Author = person!",
          successtip: "✓ WHOM ALSO WORKS!\nWHO is standard, WHOM is very formal! Both correct! 📚"
        },
        {
          type: "order",
          q: "Create: colleagues / who / My / helpful / are / very / me / always / support",
          words: ["My", "colleagues,", "who", "are", "very", "helpful,", "always", "support", "me"],
          a: "My colleagues, who are very helpful, always support me",
          extra: ["that"],
          successtip: "✓ TEAM APPRECIATION!\nDescribing your team with extra positive info! Professional! 🤝"
        },
        {
          type: "mc",
          q: "In 'Lisa, who I work with, is talented', the WHO clause tells us:",
          options: [
            "Extra info - where I work with her",
            "Which Lisa we're talking about",
            "It's not necessary information"
          ],
          a: "Extra info - where I work with her",
          tip: "Lisa is already identified!",
          successtip: "✓ CONTEXT INFO!\nAdding WHERE you know her from - helpful context! 💼"
        },
        {
          type: "match",
          q: "Match the family descriptions:",
          pairs: [
            { left: "My grandmother, who is 90,", right: "still plays piano" },
            { left: "My uncle, who lives abroad,", right: "visits every summer" },
            { left: "My cousin, who just graduated,", right: "got a job" }
          ],
          a: "completed",
          successtip: "✓ FAMILY STORIES!\nAdding rich details about family members! Conversational! 👨‍👩‍👧‍👦"
        }
      ]
    },

    {
      id: "m9-u16-s4",
      title: "Set 4: WHICH in Non-Defining Clauses 📦",
      context: `**Describing Things and Places**

"The new office, which opened last month, has amazing facilities. My laptop, which I bought last year, is still working perfectly."

Remember: Use WHICH for things and places in non-defining clauses!`,
      
      questions: [
        {
          type: "mc",
          q: "For things in non-defining clauses, use:",
          options: [
            "WHICH (never THAT)",
            "THAT (never WHICH)",
            "Either WHICH or THAT"
          ],
          a: "WHICH (never THAT)",
          tip: "THAT can't be used with commas!",
          successtip: "✓ WHICH ONLY!\nIn non-defining clauses, use WHICH for things - NEVER THAT! 📦"
        },
        {
          type: "match",
          q: "Complete with WHICH:",
          pairs: [
            { left: "The building, which is old,", right: "needs renovation" },
            { left: "This phone, which was expensive,", right: "broke already" },
            { left: "The project, which took months,", right: "was successful" }
          ],
          a: "completed",
          successtip: "✓ WHICH FOR THINGS!\nAdding descriptive details about objects and projects! 🏗️"
        },
        {
          type: "input",
          q: "Complete: The report, _____ I finished yesterday, was well received.",
          a: "which",
          alts: [],
          tip: "Report = thing!",
          successtip: "✓ WHICH IN ACTION!\nAdding timing information about your work! Professional! 📊"
        },
        {
          type: "order",
          q: "Translate: El coche, que compré el año pasado, funciona bien",
          words: ["The", "car,", "which", "I", "bought", "last", "year,", "works", "well"],
          a: "The car, which I bought last year, works well",
          extra: ["that", "who"],
          successtip: "✓ POSSESSION INFO!\nAdding when you got something - extra context! 🚗"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "The house, that is big, has five bedrooms",
            "The house, which is big, has five bedrooms",
            "Both are equally correct"
          ],
          a: "The house, that is big, has five bedrooms",
          tip: "THAT + commas = wrong!",
          successtip: "✓ NO THAT!\nNEVER use THAT with commas! Only WHICH for things! ❌"
        },
        {
          type: "match",
          q: "Add information about places:",
          pairs: [
            { left: "Paris, which I visited,", right: "is beautiful" },
            { left: "The park, which is nearby,", right: "is perfect for jogging" },
            { left: "The restaurant, which opened,", right: "is very popular" }
          ],
          a: "completed",
          successtip: "✓ PLACE DESCRIPTIONS!\nEnriching talk about locations with extra details! 🌍"
        },
        {
          type: "input",
          q: "Complete: The book, _____ won an award, is now a bestseller.",
          a: "which",
          alts: [],
          tip: "Book = thing!",
          successtip: "✓ ACHIEVEMENT INFO!\nAdding success stories to descriptions! Impressive! 🏆"
        },
        {
          type: "order",
          q: "Create: software / is / which / The / expensive / really / very / useful",
          words: ["The", "software,", "which", "is", "expensive,", "is", "very", "useful"],
          a: "The software, which is expensive, is very useful",
          extra: ["that"],
          successtip: "✓ COST VS VALUE!\nBalancing negative (expensive) with positive (useful)! Nuanced! 💻"
        },
        {
          type: "mc",
          q: "In 'The meeting, which lasted three hours, was productive', WHICH adds:",
          options: [
            "Extra detail about duration",
            "Essential info to identify the meeting",
            "Nothing important"
          ],
          a: "Extra detail about duration",
          tip: "Bonus timing information!",
          successtip: "✓ DURATION DETAIL!\nAdding how long something took - useful context! ⏰"
        },
        {
          type: "match",
          q: "Match the object descriptions:",
          pairs: [
            { left: "My phone, which is new,", right: "has great features" },
            { left: "The system, which we installed,", right: "improved efficiency" },
            { left: "This app, which is free,", right: "is very helpful" }
          ],
          a: "completed",
          successtip: "✓ TECH DESCRIPTIONS!\nAdding specifications and qualities! Modern vocabulary! 📱"
        }
      ]
    },

    {
      id: "m9-u16-s5",
      title: "Set 5: Comma Rules - Getting it Right ✏️",
      context: `**Punctuation Matters!**

✓ CORRECT: "My friend, who is a teacher, lives nearby."
✗ WRONG: "My friend who is a teacher, lives nearby." (missing first comma)
✗ WRONG: "My friend, who is a teacher lives nearby." (missing second comma)`,
      
      questions: [
        {
          type: "mc",
          q: "Where do commas go in non-defining clauses?",
          options: [
            "Before AND after the clause",
            "Only before the clause",
            "Only after the clause"
          ],
          a: "Before AND after the clause",
          tip: "Surround the extra info!",
          successtip: "✓ BOTH SIDES!\nCommas on BOTH sides surround the bonus information! 📍"
        },
        {
          type: "mc",
          q: "Which is correctly punctuated?",
          options: [
            "The manager, who is new, started Monday",
            "The manager, who is new started Monday",
            "The manager who is new, started Monday"
          ],
          a: "The manager, who is new, started Monday",
          successtip: "✓ PERFECT COMMAS!\nComma before WHO, comma after NEW! Correct! ✅"
        },
        {
          type: "match",
          q: "Fix the punctuation:",
          pairs: [
            { left: "My car which is old", right: "My car, which is old," },
            { left: "Paris, which I love", right: "Paris, which I love," },
            { left: "Tom who works here", right: "Tom, who works here," }
          ],
          a: "completed",
          tip: "Need commas on both sides!",
          successtip: "✓ COMMA CORRECTION!\nFixed all the missing commas! Punctuation expert! ✏️"
        },
        {
          type: "input",
          q: "Add commas: My sister who lives in Tokyo is visiting\nHow many commas needed?",
          a: "2",
          alts: ["two"],
          tip: "Before who and after Tokyo!",
          successtip: "✓ TWO COMMAS!\nOne before WHO, one after TOKYO! Surrounds the clause! 📌"
        },
        {
          type: "order",
          q: "Punctuate correctly: is / office / The / which / downtown / big / very",
          words: ["The", "office,", "which", "is", "downtown,", "is", "very", "big"],
          a: "The office, which is downtown, is very big",
          extra: ["that"],
          successtip: "✓ PROPER PUNCTUATION!\nCommas correctly placed around the location info! 🏢"
        },
        {
          type: "mc",
          q: "If the non-defining clause is at the END, how many commas?",
          options: [
            "One comma (before the clause)",
            "Two commas",
            "No commas needed"
          ],
          a: "One comma (before the clause)",
          tip: "Period ends the sentence!",
          successtip: "✓ END POSITION!\nOnly one comma needed when clause is at the end! Smart! 🎯"
        },
        {
          type: "match",
          q: "Identify comma errors:",
          pairs: [
            { left: "My dad, who is retired works", right: "Missing comma after 'retired'" },
            { left: "The book which I read, was good", right: "Missing comma before 'which'" },
            { left: "Sarah, who I know is here", right: "Missing comma after 'know'" }
          ],
          a: "completed",
          successtip: "✓ ERROR DETECTION!\nSpotting missing commas! Editing skills! 🔍"
        },
        {
          type: "input",
          q: "Is this correct? 'I met John, who is my neighbor.'\nAnswer: yes or no",
          a: "yes",
          alts: [],
          tip: "At the end, only one comma!",
          successtip: "✓ END CLAUSE!\nCorrect! Only one comma when clause ends the sentence! ✅"
        },
        {
          type: "order",
          q: "Fix: works / neighbor / My / who / here / is / friendly / very",
          words: ["My", "neighbor,", "who", "works", "here,", "is", "very", "friendly"],
          a: "My neighbor, who works here, is very friendly",
          extra: ["that"],
          successtip: "✓ DOUBLE COMMAS!\nSurrounded the clause perfectly! Comma mastery! 🏆"
        },
        {
          type: "mc",
          q: "Why are commas so important in non-defining clauses?",
          options: [
            "They show the information is extra/removable",
            "They make sentences look pretty",
            "They're optional - doesn't matter"
          ],
          a: "They show the information is extra/removable",
          tip: "Commas = signal!",
          successtip: "✓ COMMA PURPOSE!\nCommas signal: 'This is bonus info!' Essential punctuation! 💡"
        }
      ]
    },

    {
      id: "m9-u16-s6",
      title: "Set 6: Describing People with Rich Details 🎨",
      context: `**Adding Personality and Background**

"My colleague Maria, who has worked here for ten years, is incredibly knowledgeable. The new intern, who just graduated from Harvard, shows great promise."`,
      
      questions: [
        {
          type: "mc",
          q: "What kind of extra info can you add about people?",
          options: [
            "All of these: age, background, characteristics, achievements",
            "Only their age",
            "Only their job title"
          ],
          a: "All of these: age, background, characteristics, achievements",
          successtip: "✓ RICH DESCRIPTIONS!\nAdd ANY extra detail that enriches the description! 🌟"
        },
        {
          type: "match",
          q: "Match the types of extra information:",
          pairs: [
            { left: "who is 25 years old", right: "AGE" },
            { left: "who graduated from MIT", right: "EDUCATION" },
            { left: "who loves hiking", right: "INTERESTS/HOBBIES" }
          ],
          a: "completed",
          successtip: "✓ INFO CATEGORIES!\nDifferent types of bonus details you can add! Variety! 🎯"
        },
        {
          type: "input",
          q: "Complete: My teacher, _____ is very patient, explains things clearly.",
          a: "who",
          alts: [],
          tip: "Adding a characteristic!",
          successtip: "✓ PERSONALITY TRAIT!\nAdding character quality as extra information! Descriptive! ⭐"
        },
        {
          type: "order",
          q: "Translate: Mi jefe, que tiene mucha experiencia, me está ayudando",
          words: ["My", "boss,", "who", "has", "lots", "of", "experience,", "is", "helping", "me"],
          a: "My boss, who has lots of experience, is helping me",
          extra: ["that"],
          successtip: "✓ EXPERIENCE INFO!\nHighlighting someone's background! Professional context! 💼"
        },
        {
          type: "mc",
          q: "Which adds the most useful context?",
          options: [
            "My colleague Sarah, who speaks five languages, handles international clients",
            "My colleague Sarah handles international clients",
            "Both give the same information"
          ],
          a: "My colleague Sarah, who speaks five languages, handles international clients",
          tip: "Extra info explains WHY!",
          successtip: "✓ MEANINGFUL CONTEXT!\nThe extra info explains her qualifications! Rich detail! 🗣️"
        },
        {
          type: "match",
          q: "Add meaningful details:",
          pairs: [
            { left: "The CEO, who founded the company,", right: "is retiring" },
            { left: "My friend Tom, who I've known for years,", right: "is getting married" },
            { left: "The doctor, who specializes in cardiology,", right: "saved his life" }
          ],
          a: "completed",
          successtip: "✓ CONTEXT BUILDING!\nEach detail adds important background! Storytelling! 📖"
        },
        {
          type: "input",
          q: "Complete: The candidate, _____ impressed everyone, got the job.",
          a: "who",
          alts: [],
          tip: "Adding what she did!",
          successtip: "✓ ACTION INFO!\nDescribing what someone did - active information! 🎯"
        },
        {
          type: "order",
          q: "Create: grandmother / is / who / My / 85 / active / very / still",
          words: ["My", "grandmother,", "who", "is", "85,", "is", "still", "very", "active"],
          a: "My grandmother, who is 85, is still very active",
          extra: ["that"],
          successtip: "✓ AGE + CHARACTER!\nCombining age with impressive characteristic! Admirative! 👵"
        },
        {
          type: "mc",
          q: "'My neighbor, who moved in recently, is very friendly' - the WHO clause tells us:",
          options: [
            "When they arrived (timing)",
            "Which neighbor (identifying)",
            "Nothing useful"
          ],
          a: "When they arrived (timing)",
          tip: "RECENTLY = timing!",
          successtip: "✓ TIMELINE INFO!\nAdding when something happened - temporal context! ⏰"
        },
        {
          type: "match",
          q: "Match rich descriptions:",
          pairs: [
            { left: "who won an award", right: "ACHIEVEMENT" },
            { left: "who is always cheerful", right: "PERSONALITY" },
            { left: "who studied in Paris", right: "BACKGROUND" }
          ],
          a: "completed",
          successtip: "✓ DESCRIPTIVE VARIETY!\nMany ways to add interesting details about people! Mastery! 🎨"
        }
      ]
    },

    {
      id: "m9-u16-s7",
      title: "Set 7: Describing Places and Things 🌍",
      context: `**Adding Details About Locations and Objects**

"The conference room, which was recently renovated, has state-of-the-art equipment. Tokyo, which I visited last year, is an incredible city."`,
      
      questions: [
        {
          type: "mc",
          q: "What kind of extra info can you add about places?",
          options: [
            "Location, history, characteristics, personal experience",
            "Only the location",
            "Only the size"
          ],
          a: "Location, history, characteristics, personal experience",
          successtip: "✓ PLACE DETAILS!\nMany types of information to enrich place descriptions! 🗺️"
        },
        {
          type: "match",
          q: "Match the types of place information:",
          pairs: [
            { left: "which is in the city center", right: "LOCATION" },
            { left: "which was built in 1920", right: "HISTORY/AGE" },
            { left: "which I love visiting", right: "PERSONAL CONNECTION" }
          ],
          a: "completed",
          successtip: "✓ PLACE CATEGORIES!\nDifferent angles for describing locations! Geographic! 📍"
        },
        {
          type: "input",
          q: "Complete: The museum, _____ houses ancient artifacts, is fascinating.",
          a: "which",
          alts: [],
          tip: "Museum = place/thing!",
          successtip: "✓ CONTENT INFO!\nDescribing what a place contains! Informative! 🏛️"
        },
        {
          type: "order",
          q: "Translate: El parque, que está cerca de mi casa, es hermoso",
          words: ["The", "park,", "which", "is", "near", "my", "house,", "is", "beautiful"],
          a: "The park, which is near my house, is beautiful",
          extra: ["that", "who"],
          successtip: "✓ PROXIMITY INFO!\nAdding location relative to you! Practical detail! 🌳"
        },
        {
          type: "mc",
          q: "For objects, what extra info is most useful?",
          options: [
            "All of these: condition, age, cost, features",
            "Only the color",
            "Only the brand"
          ],
          a: "All of these: condition, age, cost, features",
          successtip: "✓ OBJECT DETAILS!\nMany useful facts about things you own or use! 📦"
        },
        {
          type: "match",
          q: "Add object information:",
          pairs: [
            { left: "My laptop, which is three years old,", right: "still works great" },
            { left: "The software, which costs $500,", right: "is worth it" },
            { left: "This car, which has low mileage,", right: "is in excellent condition" }
          ],
          a: "completed",
          successtip: "✓ OBJECT SPECS!\nAdding technical and value information! Descriptive! 💻"
        },
        {
          type: "input",
          q: "Complete: The restaurant, _____ opened last month, is already popular.",
          a: "which",
          alts: [],
          tip: "Restaurant = place!",
          successtip: "✓ OPENING INFO!\nAdding when something started - context for popularity! 🍽️"
        },
        {
          type: "order",
          q: "Create: building / which / The / is / tall / very / downtown / is",
          words: ["The", "building,", "which", "is", "downtown,", "is", "very", "tall"],
          a: "The building, which is downtown, is very tall",
          extra: ["that"],
          successtip: "✓ LOCATION + SIZE!\nCombining where and what it's like! Complete picture! 🏢"
        },
        {
          type: "mc",
          q: "'The phone, which I bought yesterday, stopped working' - this shows:",
          options: [
            "Frustration - it's new but already broken!",
            "Just neutral information",
            "Happiness with the purchase"
          ],
          a: "Frustration - it's new but already broken!",
          tip: "Context creates emotion!",
          successtip: "✓ EMOTIONAL CONTEXT!\nThe timing info adds frustration to the story! Powerful! 😤"
        },
        {
          type: "match",
          q: "Match city/country descriptions:",
          pairs: [
            { left: "Paris, which is called the City of Light,", right: "is romantic" },
            { left: "Japan, which I've always wanted to visit,", right: "fascinates me" },
            { left: "The beach, which is usually crowded,", right: "was empty today" }
          ],
          a: "completed",
          successtip: "✓ RICH DESCRIPTIONS!\nAdding culture, desires, and observations about places! 🌏"
        }
      ]
    },

    {
      id: "m9-u16-s8",
      title: "Set 8: Common Mistakes to Avoid ⚠️",
      context: `**Error Correction**

❌ WRONG: "My friend, that is a doctor, helps people." (THAT with commas)
✅ RIGHT: "My friend, who is a doctor, helps people."

❌ WRONG: "The car which is red needs repairs." (No commas - should be defining OR non-defining)`,
      
      questions: [
        {
          type: "mc",
          q: "What's the biggest mistake?",
          options: [
            "Using THAT in non-defining clauses",
            "Using WHO for people",
            "Using WHICH for things"
          ],
          a: "Using THAT in non-defining clauses",
          tip: "THAT never works with commas!",
          successtip: "✓ NO THAT!\nTHAT cannot be used in non-defining clauses! Only WHO/WHICH! ❌"
        },
        {
          type: "match",
          q: "Fix these errors:",
          pairs: [
            { left: "My car, that is old,", right: "My car, which is old," },
            { left: "Tom, which works here,", right: "Tom, who works here," },
            { left: "The book who I read,", right: "The book, which I read," }
          ],
          a: "completed",
          successtip: "✓ ERROR CORRECTION!\nFixed THAT → WHICH and WHICH → WHO! Perfect! ✅"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "My sister, that lives in Paris, is happy",
            "My sister, who lives in Paris, is happy",
            "My sister who lives in Paris is happy"
          ],
          a: "My sister, that lives in Paris, is happy",
          tip: "THAT + commas = wrong!",
          successtip: "✓ THAT ERROR!\nNever use THAT with commas! Only WHO for people! 👤"
        },
        {
          type: "input",
          q: "Correct: The phone, that is new, broke.\nReplace 'that' with: _____",
          a: "which",
          alts: [],
          tip: "Phone = thing!",
          successtip: "✓ THAT → WHICH!\nFixed the THAT error! Use WHICH for things! 📱"
        },
        {
          type: "order",
          q: "Fix the error: works / who / The / car / well / is / old / very",
          words: ["The", "car,", "which", "is", "old,", "works", "well"],
          a: "The car, which is old, works well",
          extra: ["that", "who"],
          tip: "Car = thing, not person!",
          successtip: "✓ WHO → WHICH!\nCar needs WHICH not WHO! Perfect correction! 🚗"
        },
        {
          type: "mc",
          q: "Another common error is:",
          options: [
            "Forgetting commas around the clause",
            "Using WHO for people",
            "Making sentences too long"
          ],
          a: "Forgetting commas around the clause",
          tip: "Commas are essential!",
          successtip: "✓ COMMA ERRORS!\nForgetting commas destroys the non-defining structure! ✏️"
        },
        {
          type: "match",
          q: "Identify the problems:",
          pairs: [
            { left: "My boss, that is kind", right: "Should be WHO not THAT" },
            { left: "The house which is big", right: "Missing commas" },
            { left: "Paris, who I love", right: "Should be WHICH not WHO" }
          ],
          a: "completed",
          successtip: "✓ ERROR AWARENESS!\nSpotting all types of non-defining clause errors! 🎯"
        },
        {
          type: "input",
          q: "What's wrong with: 'My friend who is a teacher is nice.'\nNeeds what? Answer: commas or nothing",
          a: "commas",
          alts: [],
          tip: "Depends on meaning!",
          successtip: "✓ COMMA DECISION!\nIf it's extra info, needs commas. If defining which friend, no commas! 💡"
        },
        {
          type: "order",
          q: "Fix all errors: office / that / My / downtown / is / big / very / is",
          words: ["My", "office,", "which", "is", "downtown,", "is", "very", "big"],
          a: "My office, which is downtown, is very big",
          extra: ["that"],
          successtip: "✓ MULTIPLE FIXES!\nChanged THAT to WHICH and added commas! Expert! 🏆"
        },
        {
          type: "mc",
          q: "The error in 'The student, which is smart, passed' is:",
          options: [
            "Should be WHO not WHICH (student = person)",
            "Should be THAT not WHICH",
            "No error - it's perfect"
          ],
          a: "Should be WHO not WHICH (student = person)",
          tip: "Student = person!",
          successtip: "✓ PERSON vs THING!\nStudents are people - use WHO not WHICH! 🎓"
        }
      ]
    },

    {
      id: "m9-u16-s9",
      title: "Set 9: Formal vs Informal Usage 🎩",
      context: `**Style Awareness**

FORMAL (written): "The candidate, whom we interviewed yesterday, was highly qualified."

INFORMAL (spoken): "The candidate, who we interviewed yesterday, was highly qualified."`,
      
      questions: [
        {
          type: "mc",
          q: "In very formal writing, for people as objects, use:",
          options: [
            "WHOM (very formal)",
            "WHO (standard)",
            "THAT (never with commas)"
          ],
          a: "WHOM (very formal)",
          tip: "WHOM = very formal!",
          successtip: "✓ FORMAL WHOM!\nWHOM is very formal - WHO is fine for most situations! 🎩"
        },
        {
          type: "match",
          q: "Match formality levels:",
          pairs: [
            { left: "The person whom I met", right: "VERY FORMAL" },
            { left: "The person who I met", right: "STANDARD" },
            { left: "The person I met", right: "INFORMAL (defining only)" }
          ],
          a: "completed",
          tip: "WHOM is most formal!",
          successtip: "✓ FORMALITY SPECTRUM!\nUnderstand when to use each form! Style awareness! 📊"
        },
        {
          type: "mc",
          q: "In everyday conversation, most people use:",
          options: [
            "WHO for all situations with people",
            "WHOM for all situations",
            "THAT for all situations"
          ],
          a: "WHO for all situations with people",
          tip: "WHO is standard!",
          successtip: "✓ EVERYDAY ENGLISH!\nWHO works for everything in normal conversation! 🗣️"
        },
        {
          type: "input",
          q: "Complete formally: The professor, _____ we respect, is retiring.\n(Use formal option)",
          a: "whom",
          alts: ["who"],
          tip: "Most formal = WHOM!",
          successtip: "✓ FORMAL CHOICE!\nWHOM is the most formal option! Academic style! 🎓"
        },
        {
          type: "order",
          q: "Make formal: we / The / consulted / whom / expert / recommended / it",
          words: ["The", "expert,", "whom", "we", "consulted,", "recommended", "it"],
          a: "The expert, whom we consulted, recommended it",
          extra: ["who", "that"],
          successtip: "✓ FORMAL STRUCTURE!\nWHOM makes it sound academic and professional! 💼"
        },
        {
          type: "mc",
          q: "Where is WHOM most commonly used?",
          options: [
            "Academic and legal writing",
            "Text messages",
            "Casual conversation"
          ],
          a: "Academic and legal writing",
          tip: "Very formal contexts!",
          successtip: "✓ FORMAL CONTEXTS!\nWHOM appears in formal, written, academic contexts! 📚"
        },
        {
          type: "match",
          q: "Choose the appropriate style:",
          pairs: [
            { left: "Academic paper", right: "Use WHOM for formality" },
            { left: "Email to friend", right: "Use WHO (standard)" },
            { left: "Business report", right: "Either WHO or WHOM" }
          ],
          a: "completed",
          successtip: "✓ CONTEXT MATCHING!\nChoose formality level based on situation! Smart! 🎯"
        },
        {
          type: "input",
          q: "Complete informally: My friend, _____ you met, is moving.\n(Standard choice)",
          a: "who",
          alts: ["whom"],
          tip: "WHO is standard!",
          successtip: "✓ STANDARD WHO!\nWHO works perfectly in normal situations! Natural! 😊"
        },
        {
          type: "mc",
          q: "Is WHOM disappearing from English?",
          options: [
            "Yes, in speech - WHO is replacing it",
            "No, everyone uses WHOM all the time",
            "WHOM never existed"
          ],
          a: "Yes, in speech - WHO is replacing it",
          tip: "Modern trend!",
          successtip: "✓ LANGUAGE EVOLUTION!\nWHOM is becoming rare in speech - WHO is taking over! 📈"
        },
        {
          type: "match",
          q: "When is WHOM still important?",
          pairs: [
            { left: "Formal exams", right: "Know it for tests" },
            { left: "Legal documents", right: "Still used formally" },
            { left: "Casual chat", right: "Not needed" }
          ],
          a: "completed",
          successtip: "✓ PRACTICAL KNOWLEDGE!\nKnow WHOM for formal situations, use WHO normally! Balance! ⚖️"
        }
      ]
    },

    {
      id: "m9-u16-s10",
      title: "Set 10: Mastery Challenge 🏆",
      context: `**Putting It All Together!**

Use non-defining clauses to add rich details. Remember:
- WHO for people, WHICH for things
- NEVER use THAT with commas
- Commas on BOTH sides (or one if at the end)
- Add meaningful extra information!`,
      
      questions: [
        {
          type: "mc",
          q: "Choose the BEST sentence:",
          options: [
            "My manager, who has 20 years of experience, mentors our team",
            "My manager, that has 20 years of experience, mentors our team",
            "My manager who has 20 years of experience mentors our team"
          ],
          a: "My manager, who has 20 years of experience, mentors our team",
          successtip: "✓ PERFECT STRUCTURE!\nWHO + commas + meaningful detail! Excellent! 🌟"
        },
        {
          type: "match",
          q: "Create rich professional descriptions:",
          pairs: [
            { left: "Our CEO, who founded the company,", right: "is very innovative" },
            { left: "The project, which took six months,", right: "was a huge success" },
            { left: "My colleague, who speaks four languages,", right: "handles international accounts" }
          ],
          a: "completed",
          successtip: "✓ PROFESSIONAL DETAILS!\nAdding context that explains capabilities! Business English! 💼"
        },
        {
          type: "input",
          q: "Complete: The conference, _____ was in Barcelona, was inspiring.",
          a: "which",
          alts: [],
          tip: "Conference = event/thing!",
          successtip: "✓ EVENT DESCRIPTION!\nAdding location as extra interesting detail! 🌍"
        },
        {
          type: "order",
          q: "Translate: Mi hermana, que es médica, trabaja en un hospital grande",
          words: ["My", "sister,", "who", "is", "a", "doctor,", "works", "at", "a", "big", "hospital"],
          a: "My sister, who is a doctor, works at a big hospital",
          extra: ["that", "which"],
          successtip: "✓ FAMILY + PROFESSION!\nCombining relationship and career information! Natural! 👨‍⚕️"
        },
        {
          type: "mc",
          q: "Which adds the MOST useful context?",
          options: [
            "Tokyo, which hosted the Olympics, is a modern city",
            "Tokyo is a modern city",
            "Tokyo, which is big, is a modern city"
          ],
          a: "Tokyo, which hosted the Olympics, is a modern city",
          tip: "Specific event > vague detail!",
          successtip: "✓ MEANINGFUL DETAIL!\nSpecific, interesting fact adds real value! Quality info! 🏅"
        },
        {
          type: "match",
          q: "Add compelling details:",
          pairs: [
            { left: "The author, who won the Nobel Prize,", right: "is speaking tonight" },
            { left: "This method, which saves 50% time,", right: "revolutionized the industry" },
            { left: "My mentor, who I've worked with for years,", right: "is retiring" }
          ],
          a: "completed",
          successtip: "✓ IMPACTFUL INFORMATION!\nEach detail adds significance and emotion! Powerful! ⚡"
        },
        {
          type: "input",
          q: "Complete: The software, _____ our team developed, won an award.",
          a: "which",
          alts: [],
          tip: "Software = thing!",
          successtip: "✓ ACHIEVEMENT HIGHLIGHT!\nAdding who created it - pride and ownership! 🏆"
        },
        {
          type: "order",
          q: "Create: book / reading / The / which / currently / fascinating / I'm / is",
          words: ["The", "book,", "which", "I'm", "currently", "reading,", "is", "fascinating"],
          a: "The book, which I'm currently reading, is fascinating",
          extra: ["that"],
          successtip: "✓ CURRENT ACTIVITY!\nAdding timing (currently) makes it relevant now! Dynamic! 📚"
        },
        {
          type: "mc",
          q: "Best description for networking:",
          options: [
            "I work for Google, which is a leader in tech innovation",
            "I work for Google, that is a company",
            "I work for Google which is big"
          ],
          a: "I work for Google, which is a leader in tech innovation",
          tip: "Impressive, specific details!",
          successtip: "✓ IMPRESSIVE DETAILS!\nHighlighting prestige and industry position! Networking gold! 🤝"
        },
        {
          type: "match",
          q: "Final mastery - all correct:",
          pairs: [
            { left: "My friend, who is brilliant,", right: "✓ WHO + commas for person" },
            { left: "The city, which I love,", right: "✓ WHICH + commas for place" },
            { left: "The boss, who everyone respects,", right: "✓ Extra meaningful info" }
          ],
          a: "completed",
          successtip: "✓ COMPLETE MASTERY!\n🏆 You've mastered non-defining clauses! WHO, WHICH, commas, meaning! 🏆"
        }
      ]
    },

    {
      id: "m9-u16-reading",
      title: "📖 Reading: Company Profile",
      context: `**PASSAGE 1: Company Introduction**

TechVision, which was founded in 2010, has become one of the most innovative companies in Silicon Valley. The CEO, who started the company in her garage, is now a billionaire. The headquarters, which are located in San Francisco, employ over 5,000 people.

The company, which specializes in artificial intelligence, has won numerous awards. Their latest product, which launched last month, has already sold over one million units. The development team, who worked on this project for three years, is celebrating their success.`,
      
      questions: [
        {
          type: "mc",
          q: "When was TechVision founded?",
          options: [
            "2010",
            "2013",
            "Last month"
          ],
          a: "2010",
          successtip: "✓ DATE EXTRACTION!\nFound the founding date! Detail oriented! 📅"
        },
        {
          type: "mc",
          q: "In 'TechVision, which was founded in 2010', the clause is:",
          options: [
            "Non-defining (extra info with commas)",
            "Defining (essential info, no commas)",
            "Neither"
          ],
          a: "Non-defining (extra info with commas)",
          tip: "Notice the commas!",
          successtip: "✓ CLAUSE IDENTIFICATION!\nRecognized non-defining structure while reading! 📚"
        },
        {
          type: "mc",
          q: "Where did the CEO start the company?",
          options: [
            "In her garage",
            "In Silicon Valley",
            "In San Francisco"
          ],
          a: "In her garage",
          successtip: "✓ DETAIL COMPREHENSION!\nCaught the humble beginnings story! 🏠"
        },
        {
          type: "mc",
          q: "How many people work at headquarters?",
          options: [
            "Over 5,000",
            "1,000",
            "One million"
          ],
          a: "Over 5,000",
          tip: "Don't confuse with units sold!",
          successtip: "✓ NUMBER DISTINCTION!\nSeparated employees from product sales! Careful reading! 👥"
        }
      ]
    },

    {
      id: "m9-u16-reading-2",
      title: "📖 Reading: Company Profile (Part 2)",
      context: `**PASSAGE 2: Employee Perspectives**

Maria Rodriguez, who joined TechVision five years ago, is now the Chief Technology Officer. "The culture here, which values innovation and creativity, is incredible," she says. Her team, which consists of 200 engineers, developed the award-winning AI system.

The company offers benefits, which include free meals and gym memberships, that make employees happy. The office building, which was designed by a famous architect, features an indoor garden and meditation rooms. Employees, who can work remotely three days a week, report high job satisfaction.`,
      
      questions: [
        {
          type: "mc",
          q: "How long has Maria worked at TechVision?",
          options: [
            "Five years",
            "Three years",
            "200 years"
          ],
          a: "Five years",
          successtip: "✓ TIMELINE TRACKING!\nFollowed the time reference! ⏰"
        },
        {
          type: "mc",
          q: "What does the culture value?",
          options: [
            "Innovation and creativity",
            "Only profits",
            "Traditional methods"
          ],
          a: "Innovation and creativity",
          successtip: "✓ COMPANY VALUES!\nIdentified what the company culture prioritizes! 🎯"
        },
        {
          type: "mc",
          q: "How many engineers are on Maria's team?",
          options: [
            "200",
            "5,000",
            "Three"
          ],
          a: "200",
          successtip: "✓ TEAM SIZE!\nFound the specific team number! 👨‍💻"
        },
        {
          type: "mc",
          q: "The office was designed by:",
          options: [
            "A famous architect",
            "Maria Rodriguez",
            "The employees"
          ],
          a: "A famous architect",
          successtip: "✓ DESIGNER IDENTIFIED!\nCaught who created the building! 🏢"
        },
        {
          type: "mc",
          q: "How often can employees work remotely?",
          options: [
            "Three days a week",
            "Five days a week",
            "Never"
          ],
          a: "Three days a week",
          successtip: "✓ WORK FLEXIBILITY!\nUnderstood the remote work policy! 💼"
        }
      ]
    },

    {
      id: "m9-u16-reading-3",
      title: "📖 Reading: Company Profile (Part 3)",
      context: `**PASSAGE 3: Future Plans**

TechVision's next project, which will launch next year, focuses on healthcare AI. The research lab, which recently opened in Boston, is hiring top scientists. Dr. James Chen, who won the Turing Award, is leading this initiative.

The company, which went public last year, saw its stock price double. Investors, who believe in the company's vision, continue to show strong support. The board of directors, who meet quarterly, are planning international expansion.

TechVision's success story, which inspires many entrepreneurs, shows that innovation and hard work pay off. The company, which started with just three employees, now has offices in twelve countries.`,
      
      questions: [
        {
          type: "mc",
          q: "What does the next project focus on?",
          options: [
            "Healthcare AI",
            "Social media",
            "Gaming"
          ],
          a: "Healthcare AI",
          successtip: "✓ FUTURE FOCUS!\nIdentified the new direction! 🏥"
        },
        {
          type: "mc",
          q: "Where is the new research lab?",
          options: [
            "Boston",
            "San Francisco",
            "Twelve countries"
          ],
          a: "Boston",
          tip: "Recently opened location!",
          successtip: "✓ NEW LOCATION!\nCaught the expansion city! 🔬"
        },
        {
          type: "mc",
          q: "What award did Dr. Chen win?",
          options: [
            "Turing Award",
            "Nobel Prize",
            "Oscar"
          ],
          a: "Turing Award",
          successtip: "✓ PRESTIGIOUS AWARD!\nRecognized the computing honor! 🏅"
        },
        {
          type: "mc",
          q: "When did the company go public?",
          options: [
            "Last year",
            "Next year",
            "In 2010"
          ],
          a: "Last year",
          successtip: "✓ IPO TIMING!\nFound when they went public! 📈"
        },
        {
          type: "mc",
          q: "How many employees did the company start with?",
          options: [
            "Three",
            "5,000",
            "Twelve"
          ],
          a: "Three",
          tip: "The humble beginning!",
          successtip: "✓ ORIGIN STORY!\nContrast between start (3) and now (5,000+)! Growth! 🚀"
        },
        {
          type: "mc",
          q: "How many countries have TechVision offices now?",
          options: [
            "Twelve",
            "Three",
            "One"
          ],
          a: "Twelve",
          successtip: "✓ GLOBAL REACH!\nUnderstood the international expansion! 🌍"
        }
      ]
    },

    {
      id: "m9-u16-review",
      title: "🔄 Unit Review: Non-Defining Relative Clauses",
      context: `**UNIT 16 REVIEW - Adding Information**

Let's review everything:

✅ **Non-defining clauses** = EXTRA information (bonus details)
✅ **Commas** = ALWAYS use them (both sides, or one at the end)
✅ **WHO** = for people (never THAT with commas)
✅ **WHICH** = for things/places (never THAT with commas)
✅ **WHOM** = very formal (for people as objects)
✅ **Purpose** = Add rich, interesting details

Now let's test your complete mastery!`,
      
      questions: [
        {
          type: "mc",
          q: "REVIEW: Non-defining clauses give:",
          options: [
            "Extra information that's nice to know",
            "Essential information to identify something",
            "Questions"
          ],
          a: "Extra information that's nice to know",
          successtip: "✓ CORE CONCEPT!\nBonus details, not essential identification! 📌"
        },
        {
          type: "mc",
          q: "REVIEW: Which is WRONG?",
          options: [
            "My car, that is old, needs repairs",
            "My car, which is old, needs repairs",
            "My car that is old needs repairs"
          ],
          a: "My car, that is old, needs repairs",
          tip: "THAT + commas = never!",
          successtip: "✓ THAT ERROR!\nTHAT cannot be used with commas! Only WHO/WHICH! ❌"
        },
        {
          type: "match",
          q: "REVIEW: Match the rules:",
          pairs: [
            { left: "Non-defining for people", right: "WHO (+ commas)" },
            { left: "Non-defining for things", right: "WHICH (+ commas)" },
            { left: "NEVER use in non-defining", right: "THAT" }
          ],
          a: "completed",
          successtip: "✓ RULE MASTERY!\nAll the key rules locked in! Perfect! 🔐"
        },
        {
          type: "input",
          q: "REVIEW: Complete: My boss, _____ is supportive, gave me a promotion.",
          a: "who",
          alts: [],
          tip: "Boss = person!",
          successtip: "✓ WHO FOR PEOPLE!\nCorrect pronoun + commas! Mastered! 👤"
        },
        {
          type: "order",
          q: "REVIEW: Fix: city / love / The / which / I / beautiful / is",
          words: ["The", "city,", "which", "I", "love,", "is", "beautiful"],
          a: "The city, which I love, is beautiful",
          extra: ["that"],
          successtip: "✓ COMPLETE STRUCTURE!\nWHICH + double commas! Perfect non-defining clause! 🌆"
        },
        {
          type: "mc",
          q: "REVIEW: Commas in non-defining clauses appear:",
          options: [
            "Before AND after the clause (or just before if at the end)",
            "Only before the clause",
            "Randomly"
          ],
          a: "Before AND after the clause (or just before if at the end)",
          successtip: "✓ COMMA RULE!\nSurround the extra info! Essential punctuation! ✏️"
        },
        {
          type: "match",
          q: "REVIEW: Classify correctly:",
          pairs: [
            { left: "Students who study hard", right: "DEFINING - no commas" },
            { left: "My students, who work hard,", right: "NON-DEFINING - commas" },
            { left: "The book that I read", right: "DEFINING - no commas" }
          ],
          a: "completed",
          successtip: "✓ CLASSIFICATION!\nKnow the difference between defining and non-defining! 🎯"
        },
        {
          type: "input",
          q: "REVIEW: For very formal writing about people as objects, use: _____",
          a: "whom",
          alts: [],
          tip: "Most formal option!",
          successtip: "✓ FORMAL GRAMMAR!\nWHOM for academic/formal contexts! Style awareness! 🎩"
        },
        {
          type: "mc",
          q: "REVIEW: What's the difference in meaning?",
          options: [
            "'My sister who lives in NYC' = I have multiple sisters. 'My sister, who lives in NYC,' = I have one sister",
            "They mean exactly the same thing",
            "The first is wrong"
          ],
          a: "'My sister who lives in NYC' = I have multiple sisters. 'My sister, who lives in NYC,' = I have one sister",
          tip: "Commas change meaning!",
          successtip: "✓ MEANING SHIFT!\nCommas completely change what you're saying! Critical! 💡"
        },
        {
          type: "match",
          q: "FINAL REVIEW: Complete mastery check:",
          pairs: [
            { left: "Extra info about people", right: "Use WHO + commas" },
            { left: "Extra info about things", right: "Use WHICH + commas" },
            { left: "Never in non-defining", right: "THAT" }
          ],
          a: "completed",
          successtip: "✓ COMPLETE MASTERY!\n🏆 Unit 16 conquered! Non-defining clauses mastered! WHO, WHICH, commas! 🏆"
        }
      ]
    }
  ]
};
